/**
 * Web 端「非秘密情报」集中配置（阈值 / flags / 可公开常量）。
 *
 * 目标：
 * - 把“会经常调、且不属于秘密情报”的值集中到一个文件，便于统一口径与管理。
 * - 所有值必须带注释。
 *
 * ⚠️ 秘密情报不要写在这里：
 * - 真正的机密（服务端密钥、私钥等）请通过部署平台环境变量注入，并在 `secrets.ts` 里说明来源。
 */

// ============================================================
// 匹配/聊天室额度
// ============================================================

/** 默认自有聊天室（accepted match，且非 shared chat）上限；共享聊天室不计入。 */
export const MAX_SELF_CHAT_ROOMS = 5;

/** 分享码长度（目前后端生成 6 位）。 */
export const SHARE_CODE_LENGTH = 6;

/** 密码最小长度（字符数）。 */
export const PASSWORD_MIN_LENGTH = 6;

/** 昵称最大长度（与 iOS/Android、数据库默认生成一致）。 */
export const DISPLAY_NAME_MAX_LENGTH = 32;

/** 前台心跳间隔：节流写入 `profiles.last_app_foreground_at`（毫秒）。 */
export const APP_FOREGROUND_HEARTBEAT_MS = 35_000;

// ============================================================
// Supabase（非秘密：浏览器端必须可见）
// ============================================================

/**
 * 运行环境标识 ('development' | 'production')。
 */
export const APP_ENV = (import.meta.env.VITE_APP_ENV as string) || (import.meta.env.DEV ? "development" : "production");
export const IS_DEV_BACKEND = APP_ENV !== "production";

/**
 * Supabase URL（非秘密）。
 * 允许通过 Vite 环境变量覆盖，便于多环境部署。
 */
export const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

/**
 * Supabase Publishable Key（非秘密，新版命名）。
 * 必须通过 Vite 环境变量注入，不包含硬编码。
 */
export const SUPABASE_PUBLISHABLE_KEY = (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
  import.meta.env.VITE_SUPABASE_ANON_KEY) as string;

// ============================================================
// Sentry（前端 DSN 通常可公开；采样率可调）
// ============================================================

/** Sentry DSN（通常可公开；若你的团队策略认为 DSN 也应隐藏，请移到 secrets.ts 并走 env 注入）。 */
export const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string;

/** 性能监测采样率（建议线上环境调低）。 */
export const SENTRY_TRACES_SAMPLE_RATE = 1.0;

/** 重放采样率：普通会话。 */
export const SENTRY_REPLAYS_SESSION_SAMPLE_RATE = 0.1;

/** 重放采样率：发生错误时。 */
export const SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE = 1.0;

/** 是否发送用户 IP 等 PII（上线前建议评估合规与隐私策略）。 */
export const SENTRY_SEND_DEFAULT_PII = true;

// ============================================================
// Android APK（公开直链；安装包不打进前端静态资源）
// ============================================================

/**
 * 官网「下载 APK」按钮使用的 URL。
 * - 默认：公开分发仓库 GitHub Releases 最新版附件；URL 最后一段必须与 Release 里附件文件名完全一致。
 * - 当前 Release 附件名为 `bamabang.apk`（仓库：Armkas/BamaBang-Releases）。
 * - 可选：在构建环境设置 `VITE_ANDROID_APK_URL` 覆盖（自建 CDN 或改名后的附件）。
 */
export const ANDROID_APK_DOWNLOAD_URL =
  (import.meta.env.VITE_ANDROID_APK_URL as string | undefined)?.trim() ||
  "https://github.com/Armkas/BamaBang-Releases/releases/latest/download/bamabang.apk";

/**
 * iOS App Store 公开链接（用于首页 SoftwareApplication JSON-LD 等 GEO 字段）。
 * 未上架或暂无链接时保持空字符串即可。
 */
export const IOS_APP_STORE_URL =
  (import.meta.env.VITE_IOS_APP_STORE_URL as string | undefined)?.trim() || "https://apps.apple.com/us/app/bamabang-serious-matchmaking/id6760888292";

// ============================================================
// 人机验证 (Cloudflare Turnstile - 网页端防护)
// ============================================================

/**
 * Cloudflare Turnstile Site Key（网页端人机验证公钥，非秘密）。
 * - 优先读取环境变量 VITE_TURNSTILE_SITE_KEY
 * - 本地开发模式（DEV）下若未配置，默认回退至 Cloudflare 官方「永远通过」的测试 Site Key：1x00000000000000000000AA
 */
export const TURNSTILE_SITE_KEY =
  (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined)?.trim() ||
  (import.meta.env.DEV ? "1x00000000000000000000AA" : "");

// ============================================================
// 网页端功能开关
// ============================================================

/**
 * 是否展示网页端注册/登录入口。
 * 支持通过 VITE_WEB_AUTH_UI_ENABLED 环境变量覆盖（"true" | "false"）。
 * 默认为 true（开启 Web 端登录注册入口并由 Turnstile 人机验证防护）。
 */
export const WEB_AUTH_UI_ENABLED =
  import.meta.env.VITE_WEB_AUTH_UI_ENABLED !== undefined
    ? import.meta.env.VITE_WEB_AUTH_UI_ENABLED === "true"
    : true;


// ============================================================
// 众筹（CAMPFIRE）
// ============================================================

/**
 * 众筹项目页链接。设置 `VITE_CROWDFUNDING_URL` 后，首页会显示众筹横幅；
 * 众筹结束后把它留空即可关闭横幅。只用在官网（Web），App 内不得出现任何指向它的入口
 * （App Store 准则 3.1.3：不得在 App 内引导用户使用 IAP 以外的购买方式）。
 */
export const CROWDFUNDING_URL =
  (import.meta.env.VITE_CROWDFUNDING_URL as string | undefined)?.trim() || "";
